import React, { useEffect, useState} from 'react';
import { Form, Modal } from 'react-bootstrap';
import styled from 'styled-components';
import { CTA } from "../../general/Button";
import RadioGroup from "../../general/RadioGroup";
import { Preferences } from "./MenuPage";

type PreferencesModalProps = {
    show: boolean;
    onHide: () => void;
    originalPreferences: Preferences;
    updatePreferences: (preferences: Preferences) => void;
};

type Allergen = {
    label: string;
    details?: string;
};

const PreferencesModal = ({
    show,
    onHide,
    originalPreferences,
    updatePreferences,
}: PreferencesModalProps) => {

    const [preferences, setPreferences] = useState<Preferences>(originalPreferences);

    const anyAllergensSet = () => {
        const { calories, glutenFree, vegan, vegetarian, halal, kosher, ...allergenPreferences } = preferences;
        return Object.values(allergenPreferences).some((element) => element === true);
    };

    const [allAllergensShow, setAllAllergensShow] = useState<boolean>(!anyAllergensSet());

    useEffect(() => {
        document.getElementById('preferences-modal')?.classList.add('anim-enter');

        return () => {
            document.getElementById('preferences-modal')?.classList.remove('anim-enter');
        };
    }, [show]);

    const handleCheckboxClick = (id: string) => {
        let updatedPrefs = { ...preferences };
        updatedPrefs[id as keyof typeof preferences] = !preferences[id as keyof typeof preferences];
        setPreferences(updatedPrefs);
    };

    const savePrefs = () => {
        updatePreferences(preferences);
        onHide();
    };

    const resetAllergens = () => {
        const { calories, glutenFree, vegan, vegetarian, halal, kosher, ...allergenPreferences } = preferences;
        const nonAllergenPreferences = { calories: calories, glutenFree: glutenFree, vegan: vegan, vegetarian: vegetarian, halal: halal, kosher: kosher };
        setPreferences(Object.assign(Object.fromEntries(Object.keys(allergenPreferences).map((key) => [key, false])), nonAllergenPreferences) as Preferences);
    };

    const allergens = [
        { label: 'celery' },
        { label: 'cereals containing gluten', details: 'such as wheat, barley and oats' },
        { label: 'crustaceans', details: 'such as prawns, crabs and lobsters' },
        { label: 'eggs' },
        { label: 'fish' },
        { label: 'lupin' },
        { label: 'milk' },
        { label: 'molluscs', details: 'such as mussels and oysters' },
        { label: 'mustard' },
        { label: 'peanuts' },
        { label: 'sesame' },
        { label: 'soybeans' },
        { label: 'sulphur dioxide and sulphites' },
        { label: 'tree nuts' },
    ];

    const renderAllergens = () => {
        return <Allergens>
            {
                allergens.map((allergen: Allergen) => {
                    return (
                        <label className="choice" key={'allergen_' + allergen.label.replaceAll(' ', '')}>
                            {renderCheckbox(allergen.label.replaceAll(' ', '-'))}
                            <span>{allergen.label.charAt(0).toUpperCase() + allergen.label.slice(1)}</span>
                        </label>
                    );
                })
            }
        </Allergens>;
    }

    const renderToggle = (id: string) => {
        return (
            <StyledToggle>
                <div className="switch-wrapper">
                    <div className="switch-row">
                        <label className="switch">
                            <input type="checkbox"
                               name={id}
                               id={id}
                               checked={preferences[id as keyof typeof preferences]}
                               onChange={ () => handleCheckboxClick(id) }
                            />
                            <span className="slider round"></span>
                        </label>
                    </div>
                </div>
            </StyledToggle>
        );
    }

    const renderCheckbox = (id: string) => {
        return (
            <CheckboxLayout>
                <StyledCheckbox>
                    <Form.Check
                        type="checkbox"
                        name={id}
                        id={id}
                        checked={preferences[id as keyof typeof preferences]}
                        onChange={ () => handleCheckboxClick(id) }
                    />
                    <CheckboxIcon
                        selected={preferences[id as keyof typeof preferences]}
                        className={preferences[id as keyof typeof preferences] ? 'anim--modifier-pulse' : ''}
                        >
                        {preferences[id as keyof typeof preferences] ?
                            <svg
                                className={'text-white'}
                                width={14}
                                height={14}
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M2.293 7.707l3.774 3.774 5.702-6.841-1.537-1.28-4.299 5.159-2.226-2.226-1.414 1.414z"
                                    fill="currentColor"
                                />
                            </svg>
                        : null}
                    </CheckboxIcon>
                </StyledCheckbox>
            </CheckboxLayout>
        );
    }

    return (
        <PreferencesModalWrapper
            show={show}
            onHide={onHide}
            size="lg"
            backdrop={true}
            centered
            id={'preferences-modal'}
            animation={false}
        >
            <Modal.Header className="modal-show-header-shadow">
                <h2 className='modal__title'>Menu settings</h2>
                <div className="modal-close__wrapper">
                    <button role="button" className={'modal-close__btn'} onClick={() => onHide()}>
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>
            </Modal.Header>
            <Modal.Body>
                <div className="preferences__list">
                    <h3 className="preference__header">Display</h3>
                    <div className="choice expanded-choice">
                        <span>Show calories</span>
                        <div>{renderToggle('calories')}</div>
                    </div>
                    {/*
                    <h2 className="preference__header">Dietary preferences</h2>
                    <div className="choice expanded-choice">
                        <span>Show gluten-free</span>
                        <div>{renderToggle('glutenFree')}</div>
                    </div>
                    <div className="choice expanded-choice">
                        <span>Show vegan</span>
                        <div>{renderToggle('vegan')}</div>
                    </div>
                    <div className="choice expanded-choice">
                        <span>Show vegetarian</span>
                        <div>{renderToggle('vegetarian')}</div>
                    </div>
                    <div className="choice expanded-choice">
                        <span>Show halal</span>
                        <div>{renderToggle('halal')}</div>
                    </div>
                    <div className="choice expanded-choice">
                        <span>Show kosher</span>
                        <div>{renderToggle('kosher')}</div>
                    </div>
                    */}
                    <h3 className="preference__header">Allergens</h3>
                    <RadioGroup selected={allAllergensShow} onChange={() => { setAllAllergensShow(true); resetAllergens() }} label="Show all items" separate={true}/>
                    <RadioGroup
                        selected={!allAllergensShow}
                        onChange={() => { setAllAllergensShow(false) }}
                        label="Hide items containing selected allergens"
                        separate={true}
                        conditionalContents={ renderAllergens() }
                    />
                </div>
            </Modal.Body>
            <Modal.Footer className="modal-show-footer-shadow">
                <div className="footerContent">
                    <CTA block onClick={savePrefs}>
                        <span>Update menu settings</span>
                    </CTA>
                </div>
            </Modal.Footer>
        </PreferencesModalWrapper>
    );
};

const PreferencesModalWrapper = styled(Modal)`
    .modal-content {
        @media screen and (max-width: 767px) {
            height: 100% !important;
        }
    }

    .modal-body {
        overflow-y: auto;

        .preferences__list {
            padding: 24px 16px;

            .preference__header {
                margin-bottom: 16px;
                font-family: var(--text_body_font);
                letter-spacing: var(--text_body_font_spacing);
                text-transform: var(--text_body_font_case);
            }

            .choice {
                display: flex;
                align-items: center;
                margin-bottom: 12px;

                span {
                    font-size: 16px;
                    line-height: 24px;
                }

                &:hover { cursor: pointer; }

                &:last-child {
                    margin-bottom: 0;
                }
            }

            .expanded-choice {
                margin-bottom: 24px;
                justify-content: space-between;
            }
        }
    }

    .modal-header {
        display: flex;
        align-items: center;
        height: 56px;
        background-color: transparent;
        z-index: 1;
        width: 100%;
        padding: 16px;

        > div { width: 100% }

        .modal__title {
            margin-bottom: 0;
            font-family: var(--text_body_font);
            letter-spacing: var(--text_body_font_spacing);
            text-transform: var(--text_body_font_case);
        }
    }

    .modal-body {
        padding: 0;
    }

    .close {
        float: left;
        opacity: 1;
    }

    .modal-footer {
        padding: 16px !important;

        .footerContent {
            width: 100%;
            display: flex;
            justify-content: center;
            align-items: center;
            margin: 0 auto;
        }
    }
`;

const Allergens = styled.div`
`;

const StyledToggle = styled.span`
    .form-check {
        display: none;
    }

    .switch-row {
        margin-bottom: 0px;

        p {
            display: inline-block;
        }

        .switch {
            float: right;
            margin-top: 0px;
        }
    }

    /* The switch - the box around the slider */

    .switch {
        position: relative;
        display: inline-block;
        width: 48px;
        height: 24px;
        margin: 0 10px;
    }

    .switch:last-child {
        margin-right: 0;
    }

    .switch:first-child {
        margin-left: 0;
    }

    /* Hide default HTML checkbox */

    .switch input {
        display: none;
    }

    /* The slider */

    .slider {
        position: absolute;
        cursor: pointer;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: var(--border-grey);
        -webkit-transition: .4s;
        transition: .4s;
    }

    .slider:before {
        position: absolute;
        content: "";
        height: 20px;
        width: 20px;
        left: 2px;
        bottom: 2px;
        background-color: white;
        transition: .4s;
    }

    input:checked+.slider {
        background-color: var(--ui_primary_cta);
    }

    input:focus+.slider {
        box-shadow: 0 0 1px #2196F3;
    }

    input:checked+.slider:before {
        transform: translateX(24px);
    }

    /* Rounded sliders */

    .slider.round {
        border-radius: 34px;
    }

    .slider.round:before {
        border-radius: 50%;
    }
`;

const CheckboxLayout = styled.div`
    line-height: 0;
`;

const StyledCheckbox = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin: 0 12px 0 0;
    .form-check {
        display: none;
    }
    width: 24px;
    height: 24px;
`;

const CheckboxIcon = styled.div<{selected: boolean}>`
    line-height: 0;
    display: inline-block;
    width: 18px;
    height: 18px;
    border: 2px solid transparent;
    transition: background-color 0.15s ease-in-out, border-color 0.15s ease-in-out;
    ${(props) => props.selected ? `
        background-color: ${props.theme.v4.ui_5};
    ` : `
     border-color: ${props.theme.v4.ui_5};`}
`;

export default PreferencesModal;
